import { isEmpty } from 'lodash';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { notNil } from '@shared/util/notNil';

export interface SystemMessageInput {
  subType?: API.MessageSubType | null;
  content: string;
  sender: { username: string };
  meta: {
    channelName?: string | null;
    users?: Array<{ username: string } | null> | null;
  };
}

const listFormatter = new Intl.ListFormat(undefined, { type: 'conjunction' });

export function toString(message: SystemMessageInput): string {
  const users = message.meta?.users?.filter(notNil) ?? [];
  const userNames = users.map(u => u.username);

  if (message.subType === 'CHANNEL_CREATED') {
    return `${message.sender.username} created this group`;
  }

  if (message.subType === 'MESSAGE_DELETED' || message.subType === 'COMMENT_DELETED') {
    return 'Message removed by sender';
  }

  if (message.subType === 'USER_JOINED') {
    return `${message.sender.username} is on board!`;
  }

  if (message.subType === 'USERS_ADDED') {
    return `${userNames.join(', ')} added by ${message.sender.username}`;
  }

  if (message.subType === 'USER_REMOVED_FROM_CHANNEL') {
    return `${userNames.join(', ')} removed by ${message.sender.username}`;
  }

  if (message.subType === 'ME_REMOVED_FROM_CHANNEL') {
    const channelName = message.meta.channelName;
    const byUser = userNames[0];
    return `You've been removed from "${channelName}" by ${byUser}`;
  }

  if (message.subType === 'USER_NOTIFIED' || message.subType === 'USERS_TEXTED') {
    // meta.users appears to include the sender as the last entry; strip it
    const recipients = userNames.slice(0, -1);

    if (isEmpty(recipients)) {
      return i18n('chat.sentBySms.noRecipients');
    }

    let items: string[];
    if (recipients.length <= 3) {
      items = recipients;
    } else {
      const numOther = recipients.length - 2;
      items = [recipients[0], recipients[1], i18n('common.nOthers', { count: numOther })];
    }

    return i18n('chat.sentBySms.withRecipients', { list: listFormatter.format(items) });
  }

  if (message.subType?.startsWith('THREAD_') || message.subType?.startsWith('CARD_')) {
    return message.content;
  }

  return '';
}
